import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Users,
  Shield,
  UserCheck,
  Clock,
  Mail,
  AlertTriangle,
  CheckCircle,
  Search,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getAllUsers, updateUserRole as apiUpdateUserRole, UserData, PaginationMetadata } from '@/api/admin';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AdminPasswordConfirmDialog } from './AdminPasswordConfirmDialog';
import { useTableSort } from '@/hooks/useTableSort';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string; newRole: string } | null>(null);
  const { toast } = useToast();

  // Pagination and search state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState<PaginationMetadata>({
    page: 1,
    limit: 50,
    total_users: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false,
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1); // Reset to first page on new search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch users when page, pageSize, or search changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize, debouncedSearch]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAllUsers(currentPage, pageSize, debouncedSearch);
      setUsers(response.users);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load user data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (userId: string, userName: string, newRole: string) => {
    setSelectedUser({ id: userId, name: userName, newRole });
    setConfirmDialogOpen(true);
  };

  const confirmRoleUpdate = async (password: string) => {
    if (!selectedUser) return;

    try {
      const result = await apiUpdateUserRole(selectedUser.id, selectedUser.newRole, password);

      toast({
        title: "Role updated",
        description: result.message,
      });

      // Refresh user list
      await fetchUsers();
    } catch (error: any) {
      console.error('Error updating role:', error);

      // Handle specific error cases
      let errorMessage = "Failed to update user role.";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      // Check for "no password set" error
      if (errorMessage.includes("User has no password set") || errorMessage.includes("no password")) {
        errorMessage = "Your admin account doesn't have a password set. Please set a password in your account settings before changing user roles.";
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
      throw error; // Re-throw to let dialog handle it
    } finally {
      setConfirmDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'moderator': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'practitioner': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'partners': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'admin': return 'Full system access and user management';
      case 'moderator': return 'Content moderation and user support';
      case 'practitioner': return 'Create sessions for clients';
      case 'partners': return 'Partner organization access';
      case 'user': return 'Standard user access';
      default: return '';
    }
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(parseInt(newSize));
    setCurrentPage(1); // Reset to first page
  };

  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedSearch('');
  };

  // Table sorting
  const { sortedData, requestSort, getSortIndicator } = useTableSort<UserData>(users);

  // Calculate display range
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, pagination.total_users);

  if (loading && users.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            User Management
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Manage user accounts, roles, and view activity statistics.
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Data Integrity Alerts */}
            {users.some(u => !u.profile_complete) && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <h3 className="font-medium text-amber-800">Data Integrity Issues</h3>
                </div>
                <div className="text-sm text-amber-700 space-y-1">
                  {users.filter(u => !u.profile_complete).length > 0 && (
                    <p>• {users.filter(u => !u.profile_complete).length} users have incomplete profiles</p>
                  )}
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                     <p className="text-sm font-medium text-blue-600">Total Users</p>
                     <p className="text-2xl font-bold text-blue-900">{pagination.total_users}</p>
                     <p className="text-xs text-blue-500">
                       {users.filter(u => u.profile_complete).length} complete profiles
                     </p>
                  </div>
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Active Users</p>
                    <p className="text-2xl font-bold text-green-900">
                      {users.filter(u => u.session_count > 0).length}
                    </p>
                  </div>
                  <UserCheck className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">Practitioners</p>
                    <p className="text-2xl font-bold text-purple-900">
                      {users.filter(u => u.role === 'practitioner' || u.role === 'partners').length}
                    </p>
                  </div>
                  <Shield className="h-8 w-8 text-purple-500" />
                </div>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600">Total Sessions</p>
                    <p className="text-2xl font-bold text-orange-900">
                      {users.reduce((sum, user) => sum + user.session_count, 0)}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-orange-500" />
                </div>
              </div>
            </div>

            {/* User Table with Scrolling */}
            <div className="border rounded-lg">
              <div className="max-h-[600px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-muted/50 z-10">
                    <TableRow>
                      <SortableTableHead
                        onSort={() => requestSort('full_name')}
                        sortDirection={getSortIndicator('full_name')}
                      >
                        User
                      </SortableTableHead>
                      <SortableTableHead
                        onSort={() => requestSort('email')}
                        sortDirection={getSortIndicator('email')}
                      >
                        Email
                      </SortableTableHead>
                      <SortableTableHead
                        onSort={() => requestSort('role')}
                        sortDirection={getSortIndicator('role')}
                      >
                        Current Role
                      </SortableTableHead>
                      <SortableTableHead
                        onSort={() => requestSort('profile_complete')}
                        sortDirection={getSortIndicator('profile_complete')}
                      >
                        Status
                      </SortableTableHead>
                      <SortableTableHead
                        onSort={() => requestSort('session_count')}
                        sortDirection={getSortIndicator('session_count')}
                      >
                        Sessions
                      </SortableTableHead>
                      <SortableTableHead
                        onSort={() => requestSort('last_session')}
                        sortDirection={getSortIndicator('last_session')}
                      >
                        Last Active
                      </SortableTableHead>
                      <SortableTableHead sortable={false}>
                        Change Role
                      </SortableTableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      // Loading skeleton
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell colSpan={7}>
                            <div className="h-12 bg-muted/20 animate-pulse rounded" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : sortedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedData.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center text-white text-sm font-medium">
                                {(user.first_name?.[0] || user.email?.[0] || 'U').toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium">
                                  {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unnamed User'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Joined {new Date(user.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{user.email || 'No email'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getRoleBadgeColor(user.role || 'user')}>
                              {user.role || 'user'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {user.profile_complete ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                              )}
                              <span className="text-sm">
                                {user.profile_complete ? 'Complete' : 'Incomplete'}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {user.session_count} sessions
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {user.last_session
                              ? new Date(user.last_session).toLocaleDateString()
                              : 'Never'
                            }
                          </TableCell>
                          <TableCell>
                            <Select
                              value={user.role}
                              onValueChange={(value) => handleRoleChange(
                                user.id,
                                user.full_name || user.email || 'User',
                                value
                              )}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">
                                  <div className="flex flex-col items-start">
                                    <span>User</span>
                                    <span className="text-xs text-muted-foreground">{getRoleDescription('user')}</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="practitioner">
                                  <div className="flex flex-col items-start">
                                    <span>Practitioner</span>
                                    <span className="text-xs text-muted-foreground">{getRoleDescription('practitioner')}</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="partners">
                                  <div className="flex flex-col items-start">
                                    <span>Partners</span>
                                    <span className="text-xs text-muted-foreground">{getRoleDescription('partners')}</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="moderator">
                                  <div className="flex flex-col items-start">
                                    <span>Moderator</span>
                                    <span className="text-xs text-muted-foreground">{getRoleDescription('moderator')}</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="admin">
                                  <div className="flex flex-col items-start">
                                    <span>Admin</span>
                                    <span className="text-xs text-muted-foreground">{getRoleDescription('admin')}</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between border-t pt-4">
              <div className="text-sm text-muted-foreground">
                {pagination.total_users > 0 ? (
                  <>Showing {startIndex}-{endIndex} of {pagination.total_users} users</>
                ) : (
                  <>No users to display</>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={!pagination.has_prev || loading}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="text-sm font-medium px-3">
                  Page {currentPage} of {pagination.total_pages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={!pagination.has_next || loading}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Confirmation Dialog */}
      <AdminPasswordConfirmDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        onConfirm={confirmRoleUpdate}
        title="Confirm Role Change"
        description={
          selectedUser
            ? `You are about to change the role of ${selectedUser.name} to ${selectedUser.newRole}. ${selectedUser.newRole ? getRoleDescription(selectedUser.newRole) : ''} This action requires admin password confirmation.`
            : "Please confirm this role change with your password."
        }
        actionLabel="Update Role"
      />
    </>
  );
};

export default UserManagement;
